import React, { useEffect, useMemo, useState } from 'react';
import AdminLayout from '@/components/AdminLayout';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
    Sparkles, Loader2, Save, Search, Check, AlertTriangle, ShieldCheck,
    DownloadCloud, FileText,
} from 'lucide-react';

const VIOLET = '#7C6BEF';
const GREEN = '#10B981';
const AMBER = '#F59E0B';
const RED = '#DC2626';
const GRAY = '#6B7280';

const CLASS_META = {
    nom: { label: 'NOM (con explicación)', bg: 'rgba(16,185,129,0.12)', fg: '#047857', icon: ShieldCheck },
    mention: { label: 'Solo mención', bg: 'rgba(14,165,233,0.12)', fg: '#0369A1', icon: FileText },
    invalid: { label: 'Cita inválida', bg: 'rgba(220,38,38,0.12)', fg: '#B91C1C', icon: AlertTriangle },
};

function ClassBadge({ cls }) {
    const m = CLASS_META[cls] || CLASS_META.invalid;
    const Icon = m.icon;
    return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full whitespace-nowrap"
            style={{ background: m.bg, color: m.fg }} data-testid={`cite-class-${cls}`}>
            <Icon className="w-3 h-3" /> {m.label}
        </span>
    );
}

function CitationCard({ c, onSaved, onFetch }) {
    const [expl, setExpl] = useState(c.explanation || '');
    const [approved, setApproved] = useState(!!c.approved);
    const [saving, setSaving] = useState(false);
    const [drafting, setDrafting] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [manualFor, setManualFor] = useState(null);
    const [manualUrl, setManualUrl] = useState('');

    useEffect(() => { setExpl(c.explanation || ''); setApproved(!!c.approved); }, [c.citation, c.explanation, c.approved]);

    const isMention = c.classification === 'mention';
    const isInvalid = c.classification === 'invalid';

    const save = async () => {
        setSaving(true);
        try {
            await api.put('/citations', { citation: c.citation, explanation: expl.trim(), approved });
            toast.success('Explicación guardada.');
            onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'No se pudo guardar.');
        } finally { setSaving(false); }
    };

    const draft = async () => {
        setDrafting(true);
        try {
            const r = await api.post('/citations/generate-draft', { citation: c.citation }, { timeout: 60000 });
            setExpl(r.data?.draft || '');
            setApproved(false);
            toast.success(`Borrador generado desde: ${(r.data?.sources_used || []).join(', ')}. Revísalo y aprueba.`);
        } catch (e) {
            const msg = e.code === 'ECONNABORTED'
                ? 'La IA tardó demasiado. Reintenta en un momento o escribe la explicación a mano.'
                : (e.response?.data?.detail || 'No se pudo generar el borrador.');
            toast.error(msg);
        } finally { setDrafting(false); }
    };

    const fetchDof = async (code, url) => {
        setFetching(true);
        try {
            const r = await api.post('/citations/fetch-dof-source', url ? { code, url } : { code });
            toast.success(`Texto oficial de ${code} cargado (${r.data?.chars} caracteres) desde el DOF.`);
            setManualFor(null); setManualUrl('');
            onFetch();
        } catch (e) {
            toast.error(e.response?.data?.detail || `No se pudo cargar el texto de ${code}.`);
            if (!url) { setManualFor(code); setManualUrl(''); }
        } finally { setFetching(false); }
    };

    const nomMissingSource = (c.nom_codes || []).filter((code) => !(c.nom_codes_with_source || []).includes(code));

    return (
        <div className="border p-4" style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }} data-testid={`cite-card-${c.citation}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <div className="font-mono font-bold text-sm break-words" style={{ color: VIOLET }}>{c.citation}</div>
                    <div className="text-[11px] text-gray-500 mt-0.5">
                        {c.item_count} ítem(s) del maestro
                        {approved && <span className="ml-2 font-bold" style={{ color: GREEN }}>· ✓ Aprobada</span>}
                    </div>
                </div>
                <ClassBadge cls={c.classification} />
            </div>

            {isInvalid && (
                <div className="mt-3 p-3 text-xs border" style={{ borderColor: GRAY, background: 'rgba(107,114,128,0.08)' }} data-testid="cite-invalid-note">
                    <div className="font-bold flex items-center gap-1" style={{ color: GRAY }}><AlertTriangle className="w-3.5 h-3.5" /> Cita corrupta</div>
                    <p className="mt-1 text-gray-500">
                        Esta cita no es válida. Revísala en el <b>Checklist Maestro</b> (marcada como “cita inválida”)
                        viendo el ítem completo en contexto, y corrígela ahí antes de redactar la explicación.
                    </p>
                    <ul className="mt-1.5 space-y-0.5 text-[11px] text-gray-500">
                        {(c.items || []).map((it) => (
                            <li key={it.item_id} className="truncate">• <b>{it.item_id}</b> — {it.question}</li>
                        ))}
                    </ul>
                </div>
            )}

            {isMention && (
                <div className="mt-3 p-2.5 text-[11px] border flex items-start gap-2" style={{ borderColor: '#0EA5E9', background: 'rgba(14,165,233,0.06)' }} data-testid="cite-mention-note">
                    <FileText className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: '#0369A1' }} />
                    <span className="text-gray-600 dark:text-gray-300">
                        <b>Solo mención — sin explicación.</b> Esta cita no es una Norma Oficial Mexicana (NFPA, ISO, guía,
                        checklist privado, etc.). Aparece citada normalmente en el reporte, pero sin texto explicativo, y
                        <b> no requiere tu aprobación</b>. Concentramos las explicaciones en las NOM.
                    </span>
                </div>
            )}

            {/* Carga de texto oficial DOF para NOMs sin fuente */}
            {!isInvalid && !isMention && nomMissingSource.length > 0 && (
                <div className="mt-3 p-2.5 text-[11px] border" style={{ borderColor: AMBER, background: 'rgba(245,158,11,0.07)' }} data-testid="cite-fetch-note">
                    <div className="font-bold mb-1.5" style={{ color: '#B45309' }}>Falta texto oficial para generar borrador con IA:</div>
                    <div className="flex flex-wrap gap-2">
                        {nomMissingSource.map((code) => (
                            <button key={code} type="button" disabled={fetching} onClick={() => fetchDof(code)}
                                data-testid={`cite-fetch-${code}`}
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold border rounded disabled:opacity-40"
                                style={{ borderColor: '#B45309', color: '#B45309' }}>
                                {fetching ? <Loader2 className="w-3 h-3 animate-spin" /> : <DownloadCloud className="w-3 h-3" />}
                                Cargar {code} (DOF)
                            </button>
                        ))}
                    </div>
                    {manualFor && (
                        <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="cite-manual-url">
                            <span className="text-[11px] font-bold" style={{ color: '#B45309' }}>Pega la URL oficial del DOF para {manualFor}:</span>
                            <input type="url" value={manualUrl} onChange={(e) => setManualUrl(e.target.value)}
                                placeholder="https://www.dof.gob.mx/nota_detalle.php?codigo=..."
                                className="flex-1 min-w-[260px] px-2 py-1 text-xs border rounded" style={{ borderColor: '#B45309' }} />
                            <button type="button" disabled={fetching || !manualUrl.trim()} onClick={() => fetchDof(manualFor, manualUrl.trim())}
                                className="px-2.5 py-1 text-[11px] font-bold border rounded disabled:opacity-40" style={{ borderColor: '#B45309', color: '#B45309' }}>
                                {fetching ? 'Cargando...' : 'Cargar con esta URL'}
                            </button>
                            <button type="button" onClick={() => { setManualFor(null); setManualUrl(''); }} className="px-2 py-1 text-[11px] text-gray-500">Cancelar</button>
                        </div>
                    )}
                </div>
            )}

            {!isInvalid && !isMention && (
                <div className="mt-3">
                    <div className="flex items-center justify-between mb-1">
                        <label className="text-[11px] uppercase tracking-wider font-bold text-gray-500">Explicación en lenguaje claro</label>
                        <button type="button" onClick={draft} disabled={drafting || !c.auto_draftable}
                                data-testid={`cite-draft-${c.citation}`}
                                title={c.auto_draftable ? 'Generar desde el texto oficial cargado (sin web)' : 'Carga primero el texto oficial de la NOM'}
                                className="text-[11px] font-bold inline-flex items-center gap-1 px-2 py-1 border rounded disabled:opacity-40"
                                style={{ borderColor: VIOLET, color: VIOLET }}>
                                {drafting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                                {drafting ? 'Generando…' : 'Generar con IA'}
                        </button>
                    </div>
                    <textarea value={expl} onChange={(e) => setExpl(e.target.value)} rows={3} maxLength={400}
                        placeholder={'Qué exige esta cita, en 2-3 líneas…'}
                        data-testid={`cite-text-${c.citation}`}
                        className="w-full px-3 py-2 text-sm border outline-none resize-none"
                        style={{ borderColor: 'var(--border)', background: 'var(--bg)' }} />
                    <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={approved} disabled={!expl.trim()}
                                onChange={(e) => setApproved(e.target.checked)}
                                data-testid={`cite-approve-${c.citation}`} className="accent-[#10B981] w-4 h-4" />
                            <span className="text-sm font-semibold" style={{ color: approved ? GREEN : 'inherit' }}>
                                {approved ? '✓ Aprobada para reportes' : 'Aprobar para reportes'}
                            </span>
                        </label>
                        <button type="button" onClick={save} disabled={saving}
                            data-testid={`cite-save-${c.citation}`}
                            className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-bold text-white disabled:opacity-50"
                            style={{ background: VIOLET }}>
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
                        </button>
                    </div>
                    {!approved && expl.trim() && (
                        <p className="text-[11px] text-amber-600 mt-1">Aún NO aparecerá en reportes hasta que la apruebes y guardes.</p>
                    )}
                </div>
            )}
        </div>
    );
}

export default function AdminCitationExplanations() {
    const [data, setData] = useState({ summary: {}, citations: [] });
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');
    const [filter, setFilter] = useState('all');

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get('/citations');
            setData(r.data || { summary: {}, citations: [] });
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al cargar citas.');
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return (data.citations || []).filter((c) => {
            if (filter !== 'all' && c.classification !== filter) return false;
            if (!needle) return true;
            return c.citation.toLowerCase().includes(needle);
        });
    }, [data, q, filter]);

    const s = data.summary || {};
    const chips = [
        { key: 'all', label: `Todas (${s.total || 0})`, color: VIOLET },
        { key: 'nom', label: `NOM (${s.nom || 0})`, color: GREEN },
        { key: 'mention', label: `Solo mención (${s.mention || 0})`, color: '#0EA5E9' },
        { key: 'invalid', label: `Inválidas (${s.invalid || 0})`, color: RED },
    ];

    const sanitizeMaster = async () => {
        if (!window.confirm('Saneamiento del maestro (Iter 107):\n\n1) Agrega NOM-154-SCFI-2005 a la Biblioteca + texto oficial del DOF\n2) Asigna citas a los 8 ítems de extintores/hidrantes\n3) Limpia citas contaminadas (incl. paréntesis de marca)\n\n¿Ejecutar ahora?')) return;
        try {
            const r = await api.post('/citations/sanitize-master');
            const d = r.data || {};
            const asg = (d.citations_assigned || []).filter((x) => x.status === 'asignado').length;
            const nf = (d.citations_assigned || []).filter((x) => x.status === 'no encontrado').length;
            const s = d.summary || {};
            toast.success(`Saneamiento listo · NOM-154: ${d.norm?.status} (DOF ${d.dof?.ok ? d.dof.chars + ' chars' : 'no cargó'}) · Citas asignadas: ${asg}${nf ? `, no encontradas: ${nf}` : ''} · Limpiadas: ${(d.cleaned || []).length}. Resumen: NOM ${s.nom}, mención ${s.mention}, inválidas ${s.invalid}.`, { duration: 12000 });
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'No se pudo ejecutar el saneamiento.');
        }
    };

    const cleanContaminated = async () => {
        try {
            const dry = await api.post('/citations/clean-contaminated', { dry_run: true });
            const found = dry.data?.found || 0;
            if (found === 0) { toast.success('No se detectaron citas contaminadas.'); return; }
            const preview = (dry.data.changes || []).slice(0, 5)
                .map((ch) => `• ${ch.before}  →  ${ch.after}`).join('\n');
            if (!window.confirm(`Se detectaron ${found} cita(s) contaminada(s):\n\n${preview}${found > 5 ? `\n… y ${found - 5} más` : ''}\n\n¿Limpiarlas ahora?`)) return;
            const res = await api.post('/citations/clean-contaminated', { dry_run: false });
            toast.success(`${res.data?.applied || 0} cita(s) limpiada(s).`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'No se pudo limpiar.');
        }
    };

    return (
        <AdminLayout title="Explicaciones por cita">
            <div className="max-w-5xl mx-auto space-y-6">
                <div>
                    <h2 className="font-display text-2xl font-black tracking-tight" style={{ color: 'var(--fg)' }}>
                        Explicaciones por cita normativa
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-3xl">
                        Cada explicación se indexa por la <b>cita exacta</b> que aparece en el Checklist Maestro (no por norma).
                        Solo las citas con <b>Norma Oficial Mexicana (NOM-*)</b> llevan explicación; el resto se menciona sin texto.
                        Aparece en el PDF/correo de un punto “No cumple” solo cuando la <b>apruebas</b>. La IA genera borradores
                        <b> únicamente</b> desde el texto oficial de NOMs cargado del DOF — nunca de internet.
                    </p>
                    <div className="flex items-center gap-3 flex-wrap mt-2">
                        <p className="text-sm font-semibold" style={{ color: GREEN }} data-testid="cite-approved-count">
                            {s.approved || 0} de {s.nom || 0} citas NOM aprobadas para reportes
                        </p>
                        <button type="button" onClick={cleanContaminated} data-testid="cite-clean-btn"
                            className="text-[11px] font-bold inline-flex items-center gap-1 px-2.5 py-1 border rounded"
                            style={{ borderColor: RED, color: RED }}>
                            <AlertTriangle className="w-3 h-3" /> Limpiar citas contaminadas
                        </button>
                        <button type="button" onClick={sanitizeMaster} data-testid="cite-sanitize-btn"
                            className="text-[11px] font-bold inline-flex items-center gap-1 px-2.5 py-1 border rounded"
                            style={{ borderColor: VIOLET, color: VIOLET }}>
                            <ShieldCheck className="w-3 h-3" /> Sanear maestro (Iter 107)
                        </button>
                    </div>
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                    <div className="relative flex-1 min-w-[220px] max-w-sm">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
                            placeholder="Buscar cita…" data-testid="cite-search"
                            className="w-full pl-9 pr-3 py-2 text-sm border outline-none"
                            style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }} />
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                        {chips.map((ch) => (
                            <button key={ch.key} onClick={() => setFilter(ch.key)}
                                data-testid={`cite-filter-${ch.key}`}
                                className="px-3 py-1.5 text-[11px] font-bold border rounded-full transition-colors"
                                style={filter === ch.key
                                    ? { background: ch.color, color: '#fff', borderColor: ch.color }
                                    : { color: ch.color, borderColor: ch.color, background: 'transparent' }}>
                                {ch.label}
                            </button>
                        ))}
                    </div>
                </div>

                {loading ? (
                    <div className="p-12 flex items-center justify-center text-gray-400">
                        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando citas…
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="p-12 text-center text-gray-400 text-sm border" style={{ borderColor: 'var(--border)' }}>
                        <FileText className="w-6 h-6 mx-auto mb-2 opacity-50" /> Sin citas que coincidan.
                    </div>
                ) : (
                    <div className="grid gap-3" data-testid="cite-list">
                        {filtered.map((c) => (
                            <CitationCard key={c.citation} c={c} onSaved={load} onFetch={load} />
                        ))}
                    </div>
                )}
            </div>
        </AdminLayout>
    );
}
